import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Selector } from "../../../components/selector/Selector";
import { PrimaryButton } from "../../../components/primary/PrimaryButton";
import { useAuth } from "../../../hooks/useAuth";
import { useNavigate } from "react-router-dom";
import { useGetLandMap } from "../sale/maps/hooks/useGetLandMap";
import {
  calculateDownPayment,
  calculateInstallments,
  calculateLandTotal,
  formattedPrice,
} from "../../../utils";
import { getLatestStep } from "../../../services/LotLandsService";
import { roles } from "../../../constants/roles";

const Items = ({ title, description, className, styleDescription }) => {
  return (
    <div
      className={`flex flex-col justify-start items-start mt-8 mr-5 ${className}`}
    >
      <p className="text-base" style={{ color: "#024E82" }}>
        {title}
      </p>
      <p className={`text-sm ${styleDescription}`}>{description}</p>
    </div>
  );
};

export const getStatus = (value) => {
  switch (value) {
    case 1:
      return "";
    case 2:
      return "Selección del terreno";
    case 3:
      return "Apartado";
    case 4:
      return "Envío de decumentos";
    case 5:
      return "Recepción de documentos";
    case 6:
      return "Aprobación de documentos";
    case 6.1:
      return "Aprobación de documentos";
    case 6.2:
      return "Aprobación de documentos";
    case 7:
      return "Contrato";
    case 7.1:
      return "Aprobación de documentos";
    case 7.2:
      return "Aprobación de documentos";
    case 8:
      return "Pago inicial";
    case 9:
      return "Pagos del terreno";
    default:
      break;
  }
};

const UserSites = () => {
  const { user } = useAuth().user;
  const authUser = user?.user;
  const navigate = useNavigate();
  const { handleLendMap, lend } = useGetLandMap();
  // const [user, setUser] = useState(null);
  const [landId, setLandId] = useState(null);
  const [maxStep, setMaxStep] = useState(null);

  const lands = useMemo(() => user?.client?.lands || [], [user?.client?.lands]);

  const landsList = useMemo(
    () => lands?.map?.((l) => ({ value: l?.id, label: l?.identifying_label })),
    [lands]
  );

  const selectedLand = useMemo(() => {
    if (!landId) {
      return null;
    }

    return lands.find((l) => l.id === landId);
  }, [landId, lands]);

  const adapterStep = useCallback(
    (step) => {
      const role = authUser?.roles?.[0]?.name?.toLowerCase();

      if (role !== roles.CLIENT && step === 4) {
        return 3.1;
      }

      if (role === roles.CLIENT && step === 4) {
        return step;
      }

      if (role !== roles.CLIENT && step === 6) {
        return 6;
      }

      if (role === roles.CLIENT && step === 6) {
        return 7;
      }

      if (role === roles.CLIENT && step === 7) {
        return 7.2;
      }

      if (role !== roles.CLIENT && step === 7) {
        return 8;
      }

      return step;
    },
    [authUser?.roles]
  );

  const navigateToProgress = useCallback(() => {
    navigate(`/admin/home`);
  }, [navigate]);

  useEffect(() => {
    if (lands.length > 0) {
      setLandId(lands[0].id);
    }

    if (selectedLand) {
      handleLendMap(selectedLand);
    }
  }, [handleLendMap, selectedLand, lands]);

  useEffect(() => {
    if (selectedLand) {
      getLatestStep({ landId: selectedLand.id }).then((res) => {
        setMaxStep(res?.data?.max_step || 0);
      });
    }
  }, [handleLendMap, selectedLand]);

  if (!user) {
    return null;
  }

  return (
    <div className="p-5 DashboardSeller max-w-screen-xl">
      <div className="flex flex-row justify-start items-center w-full mt-10">
        <h3 className="title text-2xl mr-10">Terrenos</h3>
        <div style={{ minWidth: "9rem" }}>
          <Selector
            name="land"
            options={landsList}
            defaultValue={{
              value: landsList?.[0]?.value || 0,
              label: landsList?.[0]?.label || "Seleccionar",
            }}
            onChange={(e) => {
              setLandId(e.value);
            }}
            isNewVersion
          />
        </div>
      </div>
      <div className="bg-white rounded-md">
        <div className="flex flex-row justify-start items-center w-full mt-10  p-10">
          {/* <div className="w-80 h-80 bg-gray mr-10 rounded-md">
            {lend?.render?.(
              [selectedLand],
              selectedLand,
              Number(selectedLand?.lote || 0)
            )}
          </div> */}

          <table className="w-full">
            <thead>
              <tr>
                <th></th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              <tr className="w-1/2">
                <td>
                  <Items
                    className="mt-0"
                    title="Nombre"
                    description={selectedLand?.identifying_label || ""}
                  />
                </td>
                <td>
                  <Items
                    className="mt-0"
                    title="Medidas"
                    description={selectedLand?.surface || ""}
                  />
                </td>
                <td>
                  <Items
                    className="mt-0"
                    title="Estatus"
                    description={getStatus(adapterStep(maxStep)) || ""}
                    styleDescription="font-semibold"
                  />
                </td>
              </tr>
              <tr>
                <td>
                  <Items
                    title="Adelanto"
                    description={
                      selectedLand?.id
                        ? formattedPrice(
                            calculateDownPayment(
                              calculateLandTotal(
                                selectedLand?.price,
                                selectedLand?.surface
                              )
                            )?.down_payment
                          )
                        : ""
                    }
                  />
                </td>
                <td>
                  <Items
                    title="Ubicación"
                    description={selectedLand?.location || ""}
                  />
                </td>
              </tr>
              <tr>
                <td>
                  <Items
                    title="Descuento"
                    description={selectedLand?.id ? "%0" : ""}
                  />
                </td>
                <td style={{ maxWidth: "300px" }}>
                  <Items
                    title="Descripción"
                    description={selectedLand?.description || ""}
                  />
                </td>
              </tr>
              <tr>
                <td>
                  <Items
                    title="Mensualidad"
                    description={
                      selectedLand?.id
                        ? formattedPrice(
                            calculateInstallments(
                              calculateLandTotal(
                                selectedLand?.price,
                                selectedLand?.surface
                              ),
                              calculateDownPayment(
                                calculateLandTotal(
                                  selectedLand?.price,
                                  selectedLand?.surface
                                )
                              )?.down_payment
                            )?.installment
                          ) + " (120 meses)"
                        : ""
                    }
                  />
                </td>
                <td>
                  <div className="flex flex-row justify-start items-end mt-6">
                    <PrimaryButton
                      style={{ right: 10 }}
                      onClick={navigateToProgress}
                    >
                      Progreso
                    </PrimaryButton>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>

          {/*<div className="flex flex-row justify-start items-center w-full">
            <div>
              <Items
                title="Nombre:"
                description={selectedLand?.identifying_label || ""}
              />
              <Items
                title="Adelanto:"
                description={selectedLand?.advance || ""}
              />
              <Items title="Interés:" description="" />
            </div>
            <div>
              <Items
                title="Medida:"
                description={selectedLand?.surface || ""}
              />
              <Items
                title="Ubicación:"
                description={selectedLand?.location || ""}
              />
              <Items
                title="Descripción:"
                description={selectedLand?.description || ""}
              />
            </div>
          </div>*/}
        </div>
      </div>
    </div>
  );
};

export default UserSites;
